import styles from './DirectorHoverCard.module.scss';
import user from '../../../../../../assets/user.png';
import { toTitleCase, validateDIN } from '../../../../../../utils/utils';
import { formatShareholdingPercent } from '../../../../../../utils/formatShareholdingPercent';
import { STATUS_LABEL } from '../../../../../../common/DirectorStatus/DirectorStatus';

/** What the host row knows about a person even when the management-profile roster has no match. */
export interface DirectorHoverUnmatched {
    name?: string;
    designation?: string;
    shareholdingPercentage?: number | null;
    /** The person's kid; shown only when it is an 8-digit DIN, so a PAN or slug never renders. */
    din?: string;
    isFormer?: boolean;
}

/**
 * The hover card for a director who is NOT in Management Profile.
 *
 * Every surface showing this card joins its rows to the management-profile roster, and that join can
 * legitimately miss — a person can appear as a related party, an AML/PEP hit or a bureau row without
 * ever having a management profile. Rendering nothing there reads as a broken hover, so the card
 * still opens with what the ROW itself knows (name, designation, shareholding) and says plainly that
 * the profile data is absent, rather than showing empty Tenure/LinkedIn/Directorships rows that
 * would imply the person has none.
 */
function DirectorHoverCardEmpty({ name, designation, shareholdingPercentage, din, isFormer }: DirectorHoverUnmatched) {
    const detail = [designation, isFormer && STATUS_LABEL.FORMER, formatShareholdingPercent(shareholdingPercentage, { hideWhenNull: true })]
        .filter(Boolean)
        .join(' • ');

    return (
        <div className={styles.DirectorHoverCardEmpty}>
            <div className={styles.Header}>
                <img className={styles.Photo} src={user} alt={name ?? ''} />
                <div className={styles.HeaderText}>
                    <div className={styles.Name}>{toTitleCase(name ?? '') || '-'}</div>
                    {detail && <div className={styles.EmptyDetail}>{`• ${detail}`}</div>}
                    {validateDIN(din) && <div className={styles.EmptyDetail}>{`DIN ${din}`}</div>}
                </div>
            </div>
            <div className={styles.EmptyMessage}>No data available</div>
        </div>
    );
}

export default DirectorHoverCardEmpty;
